import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from './ThemeProvider';
import { useUserData } from './UserDataContext';
import {
    Search, X, Coins, TrendingUp, TrendingDown, Shield, Ban,
    Trash2, FileText, ChevronRight, User, AlertCircle, GraduationCap, Bell, Briefcase
} from 'lucide-react';
import { UserProfile } from '../types';
import { LegionService, AdminService } from '../services/api';
import { UniversityTribeCreationModal } from './UniversityTribeCreationModal';
import { getUserAvatar } from '../utils/userUtils';
import { AccountStatsPanel } from './AccountStatsPanel';
import { ControlCenter } from './ControlCenter';
import { BusinessReviewQueue } from './business/BusinessReviewQueue';

interface LegionActionPanelProps {
    onClose: () => void;
}

export function LegionActionPanel({ onClose }: LegionActionPanelProps) {
    const { theme } = useTheme();
    const { userProfile } = useUserData();
    const [activeTab, setActiveTab] = useState<'economy' | 'governance' | 'moderation' | 'business' | 'audit'>('economy');
    const [targetUser, setTargetUser] = useState<UserProfile | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Economy state
    const [goldAmount, setGoldAmount] = useState('');
    const [goldReason, setGoldReason] = useState('');
    const [currentBalance, setCurrentBalance] = useState<number | null>(null);
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const processingRef = useRef(false);

    // Maintenance state
    const [maintenanceLoading, setMaintenanceLoading] = useState(false);

    // Governance state
    const [selectedRank, setSelectedRank] = useState<'user' | 'union' | 'watcher' | 'legion'>('user');
    const [rankReason, setRankReason] = useState('');

    // Moderation state
    const [banDuration, setBanDuration] = useState<'temporary' | 'permanent'>('temporary');
    const [banReason, setBanReason] = useState('');
    const [muteDuration, setMuteDuration] = useState<number>(1);
    const [muteReason, setMuteReason] = useState('');
    const [contentId, setContentId] = useState('');
    const [contentType, setContentType] = useState<'yap' | 'forum' | 'reply'>('yap');
    const [deleteReason, setDeleteReason] = useState('');
    const [accountDeleteReason, setAccountDeleteReason] = useState('');
    const [accountDeleteConfirm, setAccountDeleteConfirm] = useState('');

    // University Tribes — state is declared before the rank guard so hooks rules are satisfied
    const [showUniversityModal, setShowUniversityModal] = useState(false);

    // iOS update broadcast — state before rank guard (hooks must be unconditional).
    // The user types the App Store marketing version (e.g. "1.0", "1.5") into
    // iosNotifVersion. The "already sent" check + send call both key off whatever
    // version the user typed, so each release has its own one-time-send slot.
    const [iosNotifVersion, setIosNotifVersion] = useState<string>('');
    const [iosNotifSent, setIosNotifSent] = useState<boolean | null>(false);
    const [iosNotifLoading, setIosNotifLoading] = useState(false);

    // Re-check the "already sent" guard each time the version field changes.
    // Empty version → not-sent (button enabled until they type something).
    useEffect(() => {
        const trimmed = iosNotifVersion.trim();
        if (!trimmed) {
            setIosNotifSent(false);
            return;
        }
        let cancelled = false;
        setIosNotifSent(null); // loading state for this version
        (async () => {
            try {
                const { getDbSafe } = await import('../services/firebaseConfig');
                const { getDoc, doc } = await import('firebase/firestore');
                const db = await getDbSafe();
                const slug = trimmed.replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 32) || 'unknown';
                const snap = await getDoc(doc(db, 'announcements', `ios_update_${slug}`));
                if (!cancelled) setIosNotifSent(snap.exists() && snap.data()?.sent === true);
            } catch {
                if (!cancelled) setIosNotifSent(false);
            }
        })();
        return () => { cancelled = true; };
    }, [iosNotifVersion]);

    // Only render for Legion users
    if (userProfile?.rank !== 'legion') {
        return null;
    }

    const [searchResults, setSearchResults] = useState<UserProfile[]>([]);

    const searchUsers = async (query: string) => {
        if (!query.trim() || query.length < 2) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        const result = await LegionService.searchUsers(query);
        setIsSearching(false);
        if (result.success && result.users) {
            setSearchResults(result.users.slice(0, 3)); // Reverted to 3
        } else {
            setSearchResults([]);
        }
    };

    const fetchUserBalance = async (userId: string) => {
        if (userId === 'ALL_USERS') {
            setCurrentBalance(null);
            return;
        }
        setLoadingBalance(true);
        const result = await LegionService.getUserGoldBalance(userId);
        setLoadingBalance(false);
        if (result.success) {
            setCurrentBalance(result.balance || 0);
        } else {
            setCurrentBalance(null);
        }
    };

    const handleAdjustGold = async () => {
        if (isProcessing || processingRef.current) return;
        if (!targetUser || !goldAmount || !goldReason) {
            alert('Please select a user, enter an amount, and provide a reason.');
            return;
        }

        setIsProcessing(true);
        processingRef.current = true;

        if (targetUser.id === 'ALL_USERS') {
             if (!window.confirm(`⚠️ WARNING: You are about to give ${goldAmount} gold to EVERY user. This cannot be undone. Are you sure?`)) {
                 setIsProcessing(false);
                 return;
             }
             
             try {
                // Dynamic import for batch operations
                const { getDbSafe } = await import('../services/firebaseConfig');
                const { collection, getDocs, writeBatch, doc, serverTimestamp } = await import('firebase/firestore');
                
                const db = await getDbSafe();
                const usersRef = collection(db, 'users');
                const snapshot = await getDocs(usersRef);
                
                if (snapshot.empty) {
                    alert("No users found.");
                    setIsProcessing(false);
                    return;
                }

                const amount = parseInt(goldAmount);
                const BATCH_SIZE = 200; // Reduced to 200 because we do 2 writes per user (update + notification), max 500 writes/batch
                const chunks = [];
                const docs = snapshot.docs;

                for (let i = 0; i < docs.length; i += BATCH_SIZE) {
                    chunks.push(docs.slice(i, i + BATCH_SIZE));
                }

                let updatedCount = 0;

                for (const chunk of chunks) {
                    const batch = writeBatch(db);
                    chunk.forEach(userDoc => {
                        const userRef = doc(db, 'users', userDoc.id);
                        const userData = userDoc.data();
                        const current = userData.goldBalance || userData.gold || 0;
                        const newBal = current + amount;
                        
                        // 1. Update Gold
                        batch.update(userRef, { 
                            goldBalance: newBal,
                            gold: newBal
                        });

                        // 2. Add Notification
                        const notifRef = doc(collection(db, 'notifications'));
                        batch.set(notifRef, {
                            userId: userDoc.id,
                            type: 'system',
                            title: 'Gold Deposit',
                            message: `You received ${amount} gold! Reason: ${goldReason}`,
                            read: false,
                            timestamp: new Date(), // Force client-side date for immediate visibility
                            userName: 'System',
                            userAvatar: 'https://placehold.co/400@2x.png?text=S&font=roboto',
                            metadata: {
                                amount,
                                reason: goldReason,
                                type: 'global_grant'
                            }
                        });
                    });
                    await batch.commit();
                    updatedCount += chunk.length;
                }
                alert(`✅ Successfully added ${amount} gold to ${updatedCount} users! \nEveryone has been notified.`);
                setGoldAmount('');
                setGoldReason('');
             } catch (e: any) {
                 console.error(e);
                 alert(`❌ Batch update failed: ${e.message}`);
             } finally {
                 setIsProcessing(false);
                 processingRef.current = false;
             }
             return;
        }

        try {
            const result = await LegionService.adjustGold(
                targetUser.id!.toString(),
                parseInt(goldAmount),
                goldReason
            );

            if (result.success) {
                alert(`✅ Gold adjusted successfully!\nNew balance: ${result.newBalance}\nAudit ID: ${result.auditId}`);
                setGoldAmount('');
                setGoldReason('');
                // Refresh balance
                if (targetUser && targetUser.id) {
                    fetchUserBalance(targetUser.id.toString());
                }

                // Send Notification for individual adjustment
                try {
                   const { getDbSafe } = await import('../services/firebaseConfig');
                   const { collection, addDoc } = await import('firebase/firestore');
                   const db = await getDbSafe();
                   
                   await addDoc(collection(db, 'notifications'), {
                       userId: targetUser.id!.toString(),
                       type: 'system',
                       title: 'Gold Deposit',
                       message: `You received ${goldAmount} gold! Reason: ${goldReason}`,
                       read: false,
                       timestamp: new Date(),
                       userName: 'System',
                       userAvatar: 'https://placehold.co/400@2x.png?text=S&font=roboto',
                       metadata: {
                           amount: parseInt(goldAmount),
                           reason: goldReason,
                           type: 'direct_grant'
                       }
                   });
                } catch (notifErr) {
                   console.error("Failed to send individual notification:", notifErr);
                }
            } else {
                alert(`❌ Failed to adjust gold:\n${result.error}`);
            }
        } catch (e) {
            console.error(e);
            alert("An error occurred.");
        } finally {
            setIsProcessing(false);
            processingRef.current = false;
        }
    };

    const handleChangeRank = async () => {
        if (!targetUser || !rankReason) {
            alert('Please select a user and provide a reason.');
            return;
        }
        if (targetUser.id === 'ALL_USERS') {
            alert("❌ Bulk rank changes are not supported via this panel.");
            return;
        }

        const result = await LegionService.changeRank(
            targetUser.id!.toString(),
            selectedRank,
            rankReason
        );

        if (result.success) {
            alert(`✅ Rank changed successfully!\nOld rank: ${result.oldRank}\nNew rank: ${result.newRank}\nAudit ID: ${result.auditId}`);
            setRankReason('');
            // Update target user display
            setTargetUser({ ...targetUser, rank: selectedRank });
        } else {
            alert(`❌ Failed to change rank:\n${result.error}`);
        }
    };

    const handleChangeRankWithLock = async () => {
        if (isProcessing || processingRef.current) return;
        setIsProcessing(true);
        processingRef.current = true;
        try {
            await handleChangeRank();
        } finally {
            setIsProcessing(false);
            processingRef.current = false;
        }
    };

    const handleBanUser = async () => {
        if (!targetUser || !banReason) {
            alert('Please select a user and provide a reason.');
            return;
        }
        if (targetUser.id === 'ALL_USERS') {
            alert("❌ Bulk banning is not supported via this panel.");
            return;
        }

        const result = await LegionService.banUser(
            targetUser.id!.toString(),
            banDuration,
            banReason
        );

        if (result.success) {
            const expiryMsg = result.banExpiry ? `Expires: ${result.banExpiry.toLocaleString()}` : 'Permanent';
            alert(`✅ User banned successfully!\n${expiryMsg}\nAudit ID: ${result.auditId}`);
            setBanReason('');
        } else {
            alert(`❌ Failed to ban user:\n${result.error}`);
        }
    };

    const handleBanUserWithLock = async () => {
        if (isProcessing || processingRef.current) return;
        setIsProcessing(true);
        processingRef.current = true;
        try {
            await handleBanUser();
        } finally {
            setIsProcessing(false);
            processingRef.current = false;
        }
    };

    const handleMuteUser = async () => {
        if (!targetUser || !muteReason) {
            alert('Please select a user and provide a reason.');
            return;
        }
        if (targetUser.id === 'ALL_USERS') {
            alert("❌ Bulk muting is not supported via this panel.");
            return;
        }

        const result = await LegionService.muteUser(
            targetUser.id!.toString(),
            muteDuration,
            muteReason
        );

        if (result.success) {
            alert(`✅ User muted successfully until: ${result.muteExpiry?.toLocaleString()}\nAudit ID: ${result.auditId}`);
            setMuteReason('');
        } else {
            alert(`❌ Failed to mute user:\n${result.error}`);
        }
    };

    const handleMuteUserWithLock = async () => {
        if (isProcessing || processingRef.current) return;
        setIsProcessing(true);
        processingRef.current = true;
        try {
            await handleMuteUser();
        } finally {
            setIsProcessing(false);
            processingRef.current = false;
        }
    };

    // The username, bare, as the confirmation the moderator has to type.
    const deleteConfirmTarget = String(targetUser?.username || '').replace(/^@+/, '').toLowerCase();

    const handleDeleteAccount = async () => {
        if (!targetUser || targetUser.id === 'ALL_USERS' || !accountDeleteReason) return;
        if (accountDeleteConfirm.replace(/^@+/, '').trim().toLowerCase() !== deleteConfirmTarget) {
            alert('Type the username exactly to confirm.');
            return;
        }
        try {
            await AdminService.deleteAccountAsModerator(targetUser.id!.toString(), accountDeleteReason);
            alert(`✅ @${deleteConfirmTarget} is off the platform. Their posts, chats and media are being purged in the background.`);
            setAccountDeleteReason('');
            setAccountDeleteConfirm('');
            setTargetUser(null);
        } catch (e: any) {
            alert(`❌ Failed to delete account:
${e?.message || e}`);
        }
    };

    const handleDeleteAccountWithLock = async () => {
        if (isProcessing || processingRef.current) return;
        setIsProcessing(true);
        processingRef.current = true;
        try {
            await handleDeleteAccount();
        } finally {
            setIsProcessing(false);
            processingRef.current = false;
        }
    };

    const handleDeleteContent = async () => {
        if (!contentId || !deleteReason) {
            alert('Please enter content ID and provide a reason.');
            return;
        }

        const result = await LegionService.deleteContent(
            contentId,
            contentType,
            deleteReason
        );

        if (result.success) {
            alert(`✅ Content deleted successfully!\nContent ID: ${contentId}\nAudit ID: ${result.auditId}`);
            setContentId('');
            setDeleteReason('');
        } else {
            alert(`❌ Failed to delete content:\n${result.error}`);
        }
    };

    const handleDeleteContentWithLock = async () => {
        if (isProcessing || processingRef.current) return;
        setIsProcessing(true);
        processingRef.current = true;
        try {
            await handleDeleteContent();
        } finally {
            setIsProcessing(false);
            processingRef.current = false;
        }
    };

    const handleExtendEvents = async () => {
        if (maintenanceLoading) return;
        if (!window.confirm("Extend 'What is AfroYap?' and 'Gold' events for another 3 months?")) return;

        setMaintenanceLoading(true);
        try {
            const { getDbSafe } = await import('../services/firebaseConfig');
            const { collection, getDocs, updateDoc, doc, Timestamp } = await import('firebase/firestore');
            
            const db = await getDbSafe();
            const eventsRef = collection(db, 'events');
            const snapshot = await getDocs(eventsRef);

            if (snapshot.empty) {
                alert("No events found in database.");
                return;
            }

            // Expiry date: 90 days from now
            const threeMonths = new Date();
            threeMonths.setDate(threeMonths.getDate() + 90);
            // Ensure it hits the YapDay boundary (05:00 UTC)
            threeMonths.setUTCHours(5, 0, 0, 0);

            let updatedCount = 0;
            const updates = [];

            snapshot.forEach((evDoc) => {
                const data = evDoc.data();
                const title = (data.title || '').toLowerCase();
                
                // Match "What is AfroYap" or anything with "Gold"
                if (title.includes('afroyap') || title.includes('gold')) {
                    updates.push(updateDoc(doc(db, 'events', evDoc.id), {
                        expiresAt: Timestamp.fromDate(threeMonths),
                        status: 'active', // Ensure it's back to active
                        updatedAt: new Date(),
                        updatedBy: userProfile?.id || 'legion_remote'
                    }));
                    updatedCount++;
                }
            });

            if (updatedCount === 0) {
                alert("Did not find any events matching 'AfroYap' or 'Gold'.");
            } else {
                await Promise.all(updates);
                alert(`✅ Successfully extended ${updatedCount} event(s) to ${threeMonths.toLocaleDateString()}!`);
            }
        } catch (e: any) {
            console.error(e);
            alert(`❌ Maintenance failed: ${e.message}`);
        } finally {
            setMaintenanceLoading(false);
        }
    };

    const handleSendIosNotification = async () => {
        const version = iosNotifVersion.trim();
        if (!version) {
            alert('Enter the App Store version (e.g. "1.0", "1.5") before sending.');
            return;
        }
        if (!window.confirm(`Push a notification to all iOS users letting them know AfroYap ${version} is available on the App Store?`)) return;
        setIosNotifLoading(true);
        try {
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const { getApp } = await import('firebase/app');
            const fns = getFunctions(getApp());
            const fn = httpsCallable(fns, 'sendIosUpdateNotification');
            // Pass the user-typed App Store version. The cloud function scopes
            // its one-time-send guard to this version so each release gets its
            // own broadcast slot.
            const result = await fn({ version });
            const data = result.data as any;
            alert(`✅ Sent! ${data.successCount} delivered, ${data.failureCount} failed (${data.totalTokens} tokens total).`);
            setIosNotifSent(true);
        } catch (e: any) {
            alert(`❌ Failed: ${e.message}`);
        } finally {
            setIosNotifLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{
                zIndex: 999999,
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(8px)',
                pointerEvents: 'auto'
            }}
            onClick={onClose}
        >
            <div
                className="rounded-xl shadow-2xl border-2 max-w-2xl w-full overflow-hidden flex flex-col"
                style={{
                    backgroundColor: theme === 'dark' ? '#1a1a1a' : '#fdfcf0',
                    borderColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                    // Inline, because neither max-h-[90vh] nor min-h-0 exists in
                    // the committed pre-compiled index.css. Without the cap the
                    // panel grew past the viewport; without minHeight:0 on the
                    // scrolling children below, a flex item refuses to shrink
                    // under its content and overflow-y-auto never engages. The
                    // account stats made the header tall enough to expose both.
                    maxHeight: '90vh',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b" style={{
                    borderColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                }}>
                    <div className="flex items-center gap-2">
                        <Shield className="w-6 h-6" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }} />
                        <h2 className="text-xl font-bold" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                            Legion Control Panel
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                        style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Target User Selection */}
                <div className="p-4 border-b" style={{
                    // NOT overflow-y-auto. The username dropdown below is
                    // absolutely positioned, and an overflow container clips it —
                    // which is exactly what hid the search results behind the
                    // header. The account stats moved into the scrolling tab area
                    // instead, so this header stays short enough not to need it.
                    flexShrink: 0,
                    borderColor: theme === 'dark' ? '#333' : '#ddd',
                }}>
                    <label className="block text-sm font-bold mb-2" style={{
                        color: theme === 'dark' ? '#FFD700' : '#ea580c',
                    }}>
                        Target User
                    </label>
                    <div className="flex gap-2">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    searchUsers(e.target.value);
                                }}
                                placeholder="Search usernames..."
                                className="w-full pl-10 pr-10 py-2 rounded-lg border focus:ring-2 focus:ring-[#FFD700] outline-none transition-all"
                                style={{
                                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                    borderColor: theme === 'dark' ? '#444' : '#ddd',
                                    color: theme === 'dark' ? '#ffffff' : '#000000',
                                }}
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}

                            {/* Search Results Dropdown */}
                            {searchResults.length > 0 && (
                                <div
                                    className="absolute top-full left-0 w-full mt-1 rounded-lg border shadow-xl overflow-hidden"
                                    style={{
                                        // Above everything else in the panel, so the
                                        // results are never painted behind a sibling.
                                        zIndex: 10000000,
                                        backgroundColor: theme === 'dark' ? '#222' : '#fff',
                                        borderColor: theme === 'dark' ? '#444' : '#ddd',
                                    }}
                                >
                                    {searchResults.map(user => (
                                        <button
                                            key={user.id}
                                            onClick={() => {
                                                setTargetUser(user);
                                                setSearchResults([]);
                                                setSearchQuery(user.username);
                                                if (user.id) fetchUserBalance(user.id.toString());
                                            }}
                                            className="w-full flex items-center gap-3 p-3 hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-left border-b last:border-0"
                                            style={{ borderColor: theme === 'dark' ? '#333' : '#eee' }}
                                        >
                                            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-800 rounded-full shrink-0 overflow-hidden border border-black/10 dark:border-white/10 flex items-center justify-center">
                                                {getUserAvatar(user) ? (
                                                    <img 
                                                        src={getUserAvatar(user)} 
                                                        alt={user.username} 
                                                        className="w-full h-full object-cover" 
                                                    />
                                                ) : (
                                                    <span className="text-xs">{user.username?.[0] || '?'}</span>
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-bold text-sm leading-tight" style={{ color: theme === 'dark' ? '#ffffff' : '#000' }}>{user.username}</p>
                                                <p className="text-[10px] opacity-60 leading-tight" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>{user.rank || 'user'}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* Target All Users Button */}
                        <button
                            onClick={() => {
                                setTargetUser({ id: 'ALL_USERS', username: 'ALL USERS (Global)', rank: 'legion' } as any);
                                setSearchQuery('');
                                setSearchResults([]);
                            }}
                             className="px-4 py-2 rounded-lg font-bold text-sm bg-red-500 hover:bg-red-600 text-white transition-colors"
                             title="Apply action to ALL users"
                        >
                            ALL USERS
                        </button>
                    </div>

                    {/* Self Selection Shortcut */}
                    {!targetUser && searchQuery.length === 0 && (
                        <button
                            onClick={() => {
                                setTargetUser(userProfile as any);
                                setSearchQuery(userProfile?.username || '');
                                fetchUserBalance(userProfile?.id?.toString() || '');
                            }}
                            className="mt-2 text-[10px] underline opacity-60 hover:opacity-100 transition-all"
                            style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}
                        >
                            Target Yourself ({userProfile?.username})
                        </button>
                    )}
                    {targetUser && (
                        <div className="mt-2 p-3 rounded-lg flex items-center justify-between" style={{
                            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f5f5f5',
                        }}>
                            <div className="flex items-center gap-3">
                                <User className="w-5 h-5" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }} />
                                <div>
                                    <p className="font-bold" style={{ color: theme === 'dark' ? '#ffffff' : '#000000' }}>
                                        {targetUser.username}
                                    </p>
                                    <p className="text-xs text-gray-500">Rank: {targetUser.rank || 'user'}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setTargetUser(null)}
                                className="text-red-500 hover:text-red-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="border-b overflow-x-auto" style={{
                    borderColor: theme === 'dark' ? '#333' : '#ddd',
                }}>
                    <div className="flex min-w-max">
                        {[
                            { id: 'economy', label: 'Economy', icon: Coins },
                            { id: 'governance', label: 'Governance', icon: Shield },
                            { id: 'moderation', label: 'Moderation', icon: Ban },
                            { id: 'business', label: 'Business', icon: Briefcase },
                            { id: 'audit', label: 'Audit', icon: FileText },
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                // The business queue is about applications, not
                                // about whichever account is selected above, so the
                                // ALL_USERS guard that gates the per-user tabs does
                                // not apply to it.
                                disabled={targetUser?.id === 'ALL_USERS' && tab.id !== 'economy' && tab.id !== 'business'}
                                className={`flex-1 flex items-center justify-center gap-2 p-3 font-bold text-sm transition-colors whitespace-nowrap ${
                                    targetUser?.id === 'ALL_USERS' && tab.id !== 'economy' && tab.id !== 'business' ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                                style={{
                                    // Breathing room so the four labels are readable
                                    // rather than crushed together.
                                    paddingLeft: 20,
                                    paddingRight: 20,
                                    minWidth: 132,
                                    backgroundColor: activeTab === tab.id
                                        ? (theme === 'dark' ? '#FFD700' : '#ea580c')
                                        : 'transparent',
                                    color: activeTab === tab.id
                                        ? (theme === 'dark' ? '#000000' : '#ffffff')
                                        : (theme === 'dark' ? '#FFD700' : '#ea580c'),
                                }}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ minHeight: 0 }}>
                    {/* Read-only numbers for the selected account. Lives here, in
                        the scrolling region, rather than in the fixed header —
                        a tall stats block there squeezed the tabs and its
                        overflow clipped the search dropdown. */}
                    {targetUser && targetUser.id && targetUser.id !== 'ALL_USERS' && (
                        <AccountStatsPanel userId={String(targetUser.id)} dark={theme === 'dark'} />
                    )}
                    {/* Economy Tab */}
                    {activeTab === 'economy' && (
                        <div className="space-y-4">
                            <div className="p-4 rounded-lg" style={{
                                backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f5f5f5',
                            }}>
                                <h3 className="font-bold mb-2 flex items-center justify-between" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                    Current Balance
                                    {targetUser && targetUser.id !== 'ALL_USERS' && (
                                        <button
                                            onClick={() => targetUser?.id && fetchUserBalance(targetUser.id.toString())}
                                            disabled={loadingBalance}
                                            className="text-[10px] underline hover:opacity-80 disabled:opacity-50"
                                        >
                                            {loadingBalance ? 'Syncing...' : 'Refresh'}
                                        </button>
                                    )}
                                </h3>
                                <p className="text-2xl font-bold" style={{ color: theme === 'dark' ? '#ffffff' : '#000000' }}>
                                    {targetUser?.id === 'ALL_USERS' 
                                        ? 'Global Operation' 
                                        : (currentBalance !== null ? `${currentBalance} gold` : 'Select a user')}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                    Amount (use negative to remove)
                                </label>
                                <input
                                    type="number"
                                    value={goldAmount}
                                    onChange={(e) => setGoldAmount(e.target.value)}
                                    placeholder="e.g., 100 or -50"
                                    className="w-full px-4 py-2 rounded-lg border"
                                    style={{
                                        backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                        borderColor: theme === 'dark' ? '#444' : '#ddd',
                                        color: theme === 'dark' ? '#ffffff' : '#000000',
                                    }}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                    Reason (required for audit)
                                </label>
                                <textarea
                                    value={goldReason}
                                    onChange={(e) => setGoldReason(e.target.value)}
                                    placeholder="Explain why this adjustment is being made..."
                                    rows={3}
                                    className="w-full px-4 py-2 rounded-lg border resize-none"
                                    style={{
                                        backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                        borderColor: theme === 'dark' ? '#444' : '#ddd',
                                        color: theme === 'dark' ? '#ffffff' : '#000000',
                                    }}
                                />
                            </div>

                            <button
                                onClick={handleAdjustGold}
                                disabled={!targetUser || !goldAmount || !goldReason || isProcessing}
                                className="w-full py-3 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                    backgroundColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                                    color: theme === 'dark' ? '#000000' : '#ffffff',
                                }}
                            >
                                {targetUser?.id === 'ALL_USERS' ? 'Execute GLOBAL Adjustment' : 'Execute Gold Adjustment'}
                            </button>

                            {/* Maintenance Section */}
                            <div className="mt-8 pt-6 border-t" style={{ borderColor: theme === 'dark' ? '#333' : '#ddd' }}>
                                <div className="flex items-center gap-2 mb-4">
                                    <AlertCircle className="w-4 h-4" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }} />
                                    <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                        Event Maintenance
                                    </h3>
                                </div>
                                <button
                                    onClick={handleExtendEvents}
                                    disabled={maintenanceLoading}
                                    className="w-full py-3 rounded-lg font-bold border-2 transition-all flex items-center justify-center gap-2 hover:bg-black/5 dark:hover:bg-white/5"
                                    style={{
                                        borderColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                                        color: theme === 'dark' ? '#FFD700' : '#ea580c',
                                    }}
                                >
                                    {maintenanceLoading ? 'Extending...' : 'Prolong Primary Events (3m)'}
                                </button>
                                <p className="text-[10px] text-center mt-2 opacity-60" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                    Targets "What is AfroYap" and any "Gold" events.
                                </p>
                            </div>

                            {/* iOS Update Broadcast */}
                            <div className="mt-6 pt-6 border-t" style={{ borderColor: theme === 'dark' ? '#333' : '#ddd' }}>
                                <div className="flex items-center gap-2 mb-4">
                                    <Bell className="w-4 h-4" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }} />
                                    <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                        iOS Broadcast
                                    </h3>
                                </div>
                                <label
                                    className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 opacity-80"
                                    style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}
                                >
                                    App Store version
                                </label>
                                <input
                                    type="text"
                                    value={iosNotifVersion}
                                    onChange={(e) => setIosNotifVersion(e.target.value)}
                                    placeholder="e.g. 1.0, 1.5"
                                    className="w-full mb-3 px-3 py-2 rounded-lg border-2 bg-transparent text-sm font-bold focus:outline-none focus:ring-2 focus:ring-offset-1"
                                    style={{
                                        borderColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                                        color: theme === 'dark' ? '#FFD700' : '#ea580c',
                                    }}
                                />
                                <button
                                    onClick={handleSendIosNotification}
                                    disabled={
                                        !iosNotifVersion.trim() ||
                                        iosNotifSent === true ||
                                        iosNotifLoading ||
                                        iosNotifSent === null
                                    }
                                    className="w-full py-3 rounded-lg font-bold border-2 transition-all flex items-center justify-center gap-2 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{
                                        borderColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                                        color: theme === 'dark' ? '#FFD700' : '#ea580c',
                                    }}
                                >
                                    <Bell className="w-4 h-4" />
                                    {!iosNotifVersion.trim()
                                        ? 'Enter version first'
                                        : iosNotifSent === null
                                        ? 'Checking…'
                                        : iosNotifSent
                                        ? `Already sent for ${iosNotifVersion.trim()}`
                                        : iosNotifLoading
                                        ? 'Sending…'
                                        : `Send "${iosNotifVersion.trim()} available" push`}
                                </button>
                                <p className="text-[10px] text-center mt-2 opacity-60" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                    {iosNotifSent
                                        ? `Already broadcast for ${iosNotifVersion.trim()}.`
                                        : 'One-time push per version. Type the App Store marketing version above.'}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Governance Tab */}
                    {activeTab === 'governance' && (
                        <div className="space-y-4">
                            {/* The queue first: this is the part of Governance a
                                moderator opens the panel FOR, so it should not sit
                                below the rank controls. */}
                            <div
                                className="rounded-xl p-3"
                                style={{ border: `1px solid ${theme === 'dark' ? '#333' : '#e5e5e5'}` }}
                            >
                                <ControlCenter
                                    dark={theme === 'dark'}
                                    onInspect={(uid, username) => {
                                        setTargetUser({ id: uid, username } as any);
                                        setSearchQuery(username);
                                        setActiveTab('moderation');
                                    }}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                    New Rank
                                </label>
                                <select
                                    value={selectedRank}
                                    onChange={(e) => setSelectedRank(e.target.value as any)}
                                    className="w-full px-4 py-2 rounded-lg border"
                                    style={{
                                        backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                        borderColor: theme === 'dark' ? '#444' : '#ddd',
                                        color: theme === 'dark' ? '#ffffff' : '#000000',
                                    }}
                                >
                                    <option value="user">User</option>
                                    <option value="union">Union</option>
                                    <option value="watcher">Watcher</option>
                                    <option value="legion">Legion</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                    Reason (required for audit)
                                </label>
                                <textarea
                                    value={rankReason}
                                    onChange={(e) => setRankReason(e.target.value)}
                                    placeholder="Explain why this rank change is being made..."
                                    rows={3}
                                    className="w-full px-4 py-2 rounded-lg border resize-none"
                                    style={{
                                        backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                        borderColor: theme === 'dark' ? '#444' : '#ddd',
                                        color: theme === 'dark' ? '#ffffff' : '#000000',
                                    }}
                                />
                            </div>

                            <button
                                onClick={handleChangeRankWithLock}
                                disabled={!targetUser || !rankReason || isProcessing}
                                className="w-full py-3 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                    backgroundColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                                    color: theme === 'dark' ? '#000000' : '#ffffff',
                                }}
                            >
                                Execute Rank Change
                            </button>

                            {/* ── University Tribes ── */}
                            <div className="mt-8 pt-6 border-t" style={{ borderColor: theme === 'dark' ? '#333' : '#ddd' }}>
                                <div className="flex items-center gap-2 mb-3">
                                    <GraduationCap className="w-4 h-4" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }} />
                                    <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                        University Tribes
                                    </h3>
                                </div>
                                <p className="text-xs mb-3 opacity-60" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                    Create an official university tribe. Ownership is hidden until transferred. All joins require approval.
                                </p>
                                <button
                                    onClick={() => setShowUniversityModal(true)}
                                    className="w-full py-3 rounded-lg font-bold border-2 transition-all flex items-center justify-center gap-2 hover:bg-black/5 dark:hover:bg-white/5"
                                    style={{
                                        borderColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                                        color: theme === 'dark' ? '#FFD700' : '#ea580c',
                                    }}
                                >
                                    <GraduationCap className="w-4 h-4" />
                                    Create University Tribe
                                </button>
                            </div>
                        </div>
                    )}

                    {/* University Tribe Creation Modal */}
                    <UniversityTribeCreationModal
                        open={showUniversityModal}
                        onOpenChange={setShowUniversityModal}
                    />

                    {/* Moderation Tab */}
                    {activeTab === 'moderation' && (
                        <div className="space-y-6">
                            {/* Ban User Section */}
                            <div className="space-y-4">
                                <h3 className="font-bold text-lg" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                    Ban User
                                </h3>

                                <div>
                                    <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                        Duration
                                    </label>
                                    <select
                                        value={banDuration}
                                        onChange={(e) => setBanDuration(e.target.value as any)}
                                        className="w-full px-4 py-2 rounded-lg border"
                                        style={{
                                            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                            borderColor: theme === 'dark' ? '#444' : '#ddd',
                                            color: theme === 'dark' ? '#ffffff' : '#000000',
                                        }}
                                    >
                                        <option value="temporary">Temporary (7 days)</option>
                                        <option value="permanent">Permanent</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                        Reason (required for audit)
                                    </label>
                                    <textarea
                                        value={banReason}
                                        onChange={(e) => setBanReason(e.target.value)}
                                        placeholder="Explain why this ban is being issued..."
                                        rows={3}
                                        className="w-full px-4 py-2 rounded-lg border resize-none"
                                        style={{
                                            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                            borderColor: theme === 'dark' ? '#444' : '#ddd',
                                            color: theme === 'dark' ? '#ffffff' : '#000000',
                                        }}
                                    />
                                </div>

                                <button
                                    onClick={handleBanUserWithLock}
                                    disabled={!targetUser || !banReason || isProcessing}
                                    className="w-full py-3 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 text-white"
                                >
                                    {/* Followed the label, not the state: this always read
                                        "7 Days" even with Permanent selected, so a permanent
                                        ban looked like a week-long one right up to the click. */}
                                    {banDuration === 'permanent' ? 'Execute Permanent Ban' : 'Execute Ban (7 Days)'}
                                </button>
                            </div>

                            {/* Mute User Section */}
                            <div className="space-y-4 pt-4 border-t" style={{
                                borderColor: theme === 'dark' ? '#333' : '#ddd',
                            }}>
                                <h3 className="font-bold text-lg" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                    Mute User
                                </h3>

                                <div>
                                    <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                        Duration
                                    </label>
                                    <select
                                        value={muteDuration}
                                        onChange={(e) => setMuteDuration(parseInt(e.target.value))}
                                        className="w-full px-4 py-2 rounded-lg border"
                                        style={{
                                            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                            borderColor: theme === 'dark' ? '#444' : '#ddd',
                                            color: theme === 'dark' ? '#ffffff' : '#000000',
                                        }}
                                    >
                                        <option value={1}>1 Hour</option>
                                        <option value={6}>6 Hours</option>
                                        <option value={8}>8 Hours</option>
                                        <option value={24}>24 Hours</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                        Reason (required for audit)
                                    </label>
                                    <textarea
                                        value={muteReason}
                                        onChange={(e) => setMuteReason(e.target.value)}
                                        placeholder="Explain why this mute is being issued..."
                                        rows={3}
                                        className="w-full px-4 py-2 rounded-lg border resize-none"
                                        style={{
                                            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                            borderColor: theme === 'dark' ? '#444' : '#ddd',
                                            color: theme === 'dark' ? '#ffffff' : '#000000',
                                        }}
                                    />
                                </div>

                                <button
                                    onClick={handleMuteUserWithLock}
                                    disabled={!targetUser || !muteReason || isProcessing}
                                    className="w-full py-3 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-orange-600 text-white"
                                >
                                    Execute Mute
                                </button>
                            </div>

                            {/* Delete Account Section */}
                            <div className="space-y-4 pt-4 border-t" style={{
                                borderColor: theme === 'dark' ? '#333' : '#ddd',
                            }}>
                                <h3 className="font-bold text-lg" style={{ color: '#dc2626' }}>
                                    Delete Account
                                </h3>
                                <p className="text-sm" style={{ color: theme === 'dark' ? '#bbb' : '#555' }}>
                                    Removes the account from the platform immediately: sign-in is disabled and the profile disappears.
                                    Posts, comments, chats and media are then purged. This cannot be undone.
                                </p>

                                <div>
                                    <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                        Reason (required for audit)
                                    </label>
                                    <textarea
                                        value={accountDeleteReason}
                                        onChange={(e) => setAccountDeleteReason(e.target.value)}
                                        placeholder="Explain why this account is being deleted..."
                                        rows={3}
                                        className="w-full px-4 py-2 rounded-lg border resize-none"
                                        style={{
                                            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                            borderColor: theme === 'dark' ? '#444' : '#ddd',
                                            color: theme === 'dark' ? '#ffffff' : '#000000',
                                        }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                        Type {deleteConfirmTarget ? `@${deleteConfirmTarget}` : 'the username'} to confirm
                                    </label>
                                    <input
                                        type="text"
                                        value={accountDeleteConfirm}
                                        onChange={(e) => setAccountDeleteConfirm(e.target.value)}
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        className="w-full px-4 py-2 rounded-lg border"
                                        style={{
                                            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                            borderColor: theme === 'dark' ? '#444' : '#ddd',
                                            color: theme === 'dark' ? '#ffffff' : '#000000',
                                        }}
                                    />
                                </div>

                                <button
                                    onClick={handleDeleteAccountWithLock}
                                    disabled={
                                        !targetUser || targetUser.id === 'ALL_USERS' || !accountDeleteReason || isProcessing
                                        || !deleteConfirmTarget
                                        || accountDeleteConfirm.replace(/^@+/, '').trim().toLowerCase() !== deleteConfirmTarget
                                    }
                                    className="w-full py-3 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed text-white"
                                    style={{ backgroundColor: '#7f1d1d' }}
                                >
                                    Permanently Delete Account
                                </button>
                            </div>

                            {/* Delete Content Section */}
                            <div className="space-y-4 pt-4 border-t" style={{
                                borderColor: theme === 'dark' ? '#333' : '#ddd',
                            }}>
                                <h3 className="font-bold text-lg" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                    Delete Content
                                </h3>

                                <div>
                                    <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                        Content Type
                                    </label>
                                    <select
                                        value={contentType}
                                        onChange={(e) => setContentType(e.target.value as any)}
                                        className="w-full px-4 py-2 rounded-lg border"
                                        style={{
                                            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                            borderColor: theme === 'dark' ? '#444' : '#ddd',
                                            color: theme === 'dark' ? '#ffffff' : '#000000',
                                        }}
                                    >
                                        <option value="yap">Yap</option>
                                        <option value="forum">Forum Post</option>
                                        <option value="reply">Reply</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                        Content ID
                                    </label>
                                    <input
                                        type="text"
                                        value={contentId}
                                        onChange={(e) => setContentId(e.target.value)}
                                        placeholder="Enter content ID..."
                                        className="w-full px-4 py-2 rounded-lg border"
                                        style={{
                                            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                            borderColor: theme === 'dark' ? '#444' : '#ddd',
                                            color: theme === 'dark' ? '#ffffff' : '#000000',
                                        }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold mb-2" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                                        Reason (required for audit)
                                    </label>
                                    <textarea
                                        value={deleteReason}
                                        onChange={(e) => setDeleteReason(e.target.value)}
                                        placeholder="Explain why this content is being deleted..."
                                        rows={3}
                                        className="w-full px-4 py-2 rounded-lg border resize-none"
                                        style={{
                                            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
                                            borderColor: theme === 'dark' ? '#444' : '#ddd',
                                            color: theme === 'dark' ? '#ffffff' : '#000000',
                                        }}
                                    />
                                </div>

                                <button
                                    onClick={handleDeleteContentWithLock}
                                    disabled={!contentId || !deleteReason || isProcessing}
                                    className="w-full py-3 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 text-white"
                                >
                                    Execute Content Deletion
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Business Tab — registration review, name/link changes,
                        and the suspend control. Lives here rather than on a new
                        admin surface, alongside the queues moderators already
                        work. */}
                    {activeTab === 'business' && (
                        <BusinessReviewQueue adminId={String(userProfile?.id || '')} />
                    )}

                    {/* Audit Tab */}
                    {activeTab === 'audit' && (
                        <div className="space-y-4">
                            <div className="p-6 rounded-lg text-center border-2 border-dashed" style={{
                                backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f5f5f5',
                                borderColor: theme === 'dark' ? '#444' : '#ddd',
                            }}>
                                <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }} />
                                <h3 className="text-lg font-bold mb-2" style={{ color: theme === 'dark' ? '#ffffff' : '#000000' }}>
                                    Moderator Dashboard
                                </h3>
                                <p className="text-sm text-gray-500 mb-6">
                                    Access the full administrative dashboard for categorical management, bulk audits, and advanced analytics.
                                </p>
                                <button
                                    onClick={() => {
                                        onClose();
                                        window.dispatchEvent(new CustomEvent('openModeratorDashboard'));
                                    }}
                                    className="px-6 py-3 rounded-xl font-bold transition-all transform hover:scale-105"
                                    style={{
                                        backgroundColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                                        color: theme === 'dark' ? '#000000' : '#ffffff',
                                    }}
                                >
                                    Open Dashboard
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Warning */}
                <div className="p-4 border-t" style={{
                    borderColor: theme === 'dark' ? '#FFD700' : '#ea580c',
                    backgroundColor: theme === 'dark' ? 'rgba(255, 215, 0, 0.1)' : 'rgba(234, 88, 12, 0.1)',
                }}>
                    <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }} />
                        <p className="text-xs" style={{ color: theme === 'dark' ? '#FFD700' : '#ea580c' }}>
                            <strong>Legion Authority:</strong> All actions are logged and require backend validation.
                            Misuse of Legion powers may result in rank revocation.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
